import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import david from "../assets/theme-images/david.png";
import fondonavidad from "../assets/theme-images/fondonavidad.jpg";
import torrijas from "../assets/theme-images/instagramtorrijas.png";
import jc from "../assets/theme-images/jc.png";
import mardeolivosfondo from "../assets/theme-images/mardeolivosfondo.png";
import mardeolivosletras from "../assets/theme-images/mardeolivosletras.png";
import mariyalvaro from "../assets/theme-images/mariyalvaro.jpeg";
import radiotelevisionceuta from "../assets/theme-images/radiotelevisionceuta.png";
import spoon from "../assets/theme-images/spoon.svg";
import videomdo from "../assets/theme-images/videomdo.mp4";

const sections = [
  { id: "tradicion", title: "Tradición con alma andaluza" },
  { id: "NEWS", title: "noticias" },
  { id: "premio", title: "Premio a la excelencia gastronómica" },
  { id: "informaticos", title: "Promoción" },
];

const SECTION_STYLE = {
  height: "calc(100vh - env(safe-area-inset-bottom))",
};

const Home = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.findIndex(
              (el) => el === entry.target,
            );
            if (idx !== -1) setActiveIndex(idx);
          }
        });
      },
      { root: containerRef.current, threshold: 0.5 },
    );

    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollTo = (idx) => {
    sectionRefs.current[idx]?.scrollIntoView({ behavior: "smooth" });
  };

  // --- VÍDEO NEWS: refs + handlers (evitar 'screen' global) ---
  const videoRef = useRef(null);

  const playVideoFullscreen = async () => {
    const v = videoRef.current;
    if (!v) return;

    try {
      v.controls = true;

      // Entrar en fullscreen
      if (v.requestFullscreen) {
        await v.requestFullscreen();
      } else if (v.webkitEnterFullscreen) {
        // Safari iOS
        v.webkitEnterFullscreen();
      }

      // Intentar bloquear orientación horizontal (si el navegador lo permite)
      const orientation = window.screen?.orientation;
      if (orientation?.lock) {
        try {
          await orientation.lock("landscape");
        } catch {
          /* ignore */
        }
      }

      await v.play();
    } catch {
      v.muted = true;
      try {
        await v.play();
      } catch {}
    }
  };

  useEffect(() => {
    const onFsChange = () => {
      const v = videoRef.current;
      if (!document.fullscreenElement && v) {
        v.pause();
        v.controls = false;
        // Desbloquear orientación si procede
        const orientation = window.screen?.orientation;
        if (orientation?.unlock) {
          try {
            orientation.unlock();
          } catch {}
        }
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // --- CUENTA ATRÁS (hasta 4 feb 2026, 20:00 CET/Ceuta) ---
  const TARGET_ISO = "2026-02-04T20:00:00+01:00";
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    done: false,
  });

  useEffect(() => {
    const target = new Date(TARGET_ISO).getTime();

    const tick = () => {
      const now = Date.now();
      let diff = target - now;

      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, done: true });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      diff %= 1000 * 60 * 60 * 24;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      diff %= 1000 * 60 * 60;
      const minutes = Math.floor(diff / (1000 * 60));
      diff %= 1000 * 60;
      const seconds = Math.floor(diff / 1000);

      setTimeLeft({ days, hours, minutes, seconds, done: false });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="relative h-screen w-full overflow-hidden"
      style={{
        backgroundImage: `url(${mardeolivosfondo})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Sticky dots nav */}
      <div className="fixed top-1/2 right-4 transform -translate-y-1/2 z-30">
        <div className="flex flex-col space-y-3 bg-white/20 backdrop-blur-md rounded-full p-3 shadow-lg">
          {sections.map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollTo(idx)}
              className={`transition-all duration-200 ease-in-out rounded-full ${
                activeIndex === idx
                  ? "w-4 h-4 bg-[#ba913d] shadow-inner"
                  : "w-3 h-3 bg-white/80"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Scrollable content */}
      <div
        ref={containerRef}
        className="h-full w-full snap-y snap-mandatory overflow-y-scroll scroll-smooth"
      >
        {/* TRADICIÓN */}
        <motion.section
          ref={(el) => (sectionRefs.current[0] = el)}
          id="tradicion"
          className="snap-start mt-6 mb-12 flex flex-col md:flex-row items-stretch relative overflow-hidden"
          style={SECTION_STYLE}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="w-full md:w-1/2 flex flex-col items-center justify-center px-6 md:px-8 py-3 z-10 bg-[#0c0c0c]/90 text-[#C19B50]"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img src={mardeolivosletras} alt="Mar de Olivos" className="" />
            <img
              src={spoon}
              alt="decoración olivo"
              className="w-12 mt-1 mb-1"
            />
            <h3 className="text-xl md:text-xl font-bold text-[#C19B50] font-serif leading-tight text-center">
              La cocina no tiene fronteras, pero ha de tener raíces.
            </h3>
            <Link
              to="/menu"
              className=" m-2 self-center inline-block bg-[#faf4ec] hover:bg-[#C19B50] text-[#503a2c] font-semibold px-6 py-3 rounded-md shadow-md transition"
            >
              Ver la Carta
            </Link>
          </motion.div>

          {/* === CUENTA ATRÁS ELEGANTE (ajustada para subir contenido en móvil) === */}
          <motion.div
            className="relative w-full overflow-hidden h-[100svh] md:h-screen"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Fondo */}
            <img
              src={fondonavidad}
              alt="Fondo"
              className="absolute inset-0 w-full h-full object-cover z-0"
            />
            {/* Vignette para legibilidad */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60 z-0" />

            {/* Contenido cuenta atrás */}
            <div className="absolute inset-0 z-10 flex items-start md:items-center justify-center px-6 pt-16 md:pt-0 pb-[max(env(safe-area-inset-bottom),0.5rem)] md:pb-8">
              <div className="text-center max-w-3xl md:translate-y-0 -translate-y-6">
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.6 }}
                  className="uppercase tracking-widest text-sm md:text-base font-bold text-[#FAF4EC]"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,.45)" }}
                >
                  Nueva carta
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.6 }}
                  className="mt-1 text-[#FAF4EC] text-2xl md:text-3xl font-semibold"
                  style={{ textShadow: "0 2px 6px rgba(0,0,0,.45)" }}
                >
                  4 de febrero
                </motion.p>

                {!timeLeft.done ? (
                  <div
                    aria-live="polite"
                    className="mt-5 md:mt-8 grid grid-cols-4 gap-3 md:gap-4"
                  >
                    {[
                      { label: "Días", value: timeLeft.days },
                      { label: "Horas", value: timeLeft.hours },
                      { label: "Min", value: timeLeft.minutes },
                      { label: "Seg", value: timeLeft.seconds },
                    ].map((t) => (
                      <div
                        key={t.label}
                        className="rounded-2xl bg-black/35 backdrop-blur-sm border border-[#C19B50]/40 shadow-lg px-3 py-4 md:px-4 md:py-5"
                      >
                        <motion.span
                          key={t.value}
                          initial={{ y: 12, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 260,
                            damping: 22,
                          }}
                          className="block text-3xl md:text-5xl font-bold text-[#FAF4EC] leading-none"
                          style={{ textShadow: "0 2px 6px rgba(0,0,0,.45)" }}
                        >
                          {String(t.value).padStart(2, "0")}
                        </motion.span>
                        <span
                          className="mt-1 block text-xs md:text-sm font-semibold uppercase tracking-widest text-[#fae6b2]"
                          style={{ textShadow: "0 1px 2px rgba(0,0,0,.35)" }}
                        >
                          {t.label}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    className="mt-6 md:mt-8 inline-block rounded-2xl bg-black/40 backdrop-blur-sm border border-[#C19B50]/50 px-6 py-4 shadow-xl"
                  >
                    <p
                      className="text-[#FAF4EC] text-xl md:text-2xl font-semibold"
                      style={{ textShadow: "0 2px 6px rgba(0,0,0,.45)" }}
                    >
                      ¡Es hoy la nueva carta!{" "}
                      <span className="text-[#C19B50]">¡Te esperamos!</span>
                    </p>
                  </motion.div>
                )}

                <motion.div
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{ scaleX: 1, opacity: 1 }}
                  transition={{ delay: 0.35, duration: 0.7, ease: "easeOut" }}
                  className="mx-auto mt-4 md:mt-6 h-[2px] w-36 md:w-56 origin-left bg-gradient-to-r from-[#C19B50] via-[#fae6b2] to-[#C19B50]"
                />
              </div>
            </div>
          </motion.div>
        </motion.section>

        {/* PREMIO */}
        <motion.section
          ref={(el) => (sectionRefs.current[1] = el)}
          id="premio"
          className="snap-start mt-6 mb-12 flex flex-col md:flex-row items-stretch relative overflow-hidden"
          style={SECTION_STYLE}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          {/* Imagen ocupa 384px (h-96) en móvil y toda la columna en desktop */}
          <motion.div
            className="w-full md:w-1/2 flex-shrink-0 h-96 md:h-full relative overflow-hidden"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src={mariyalvaro}
              alt="Premio recibido"
              className="absolute top-0 left-0 w-full h-full object-cover z-0"
            />
          </motion.div>

          {/* Texto rellena el espacio restante */}
          <motion.div
            className="w-full md:w-1/2 flex-1 flex flex-col justify-center px-6 md:px-16 pt-12 pb-20 z-10 bg-[#faf4ec] text-[#503a2c]"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="uppercase tracking-widest text-sm font-bold text-[#ba913d]">
              Reconocimiento
            </p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-serif leading-tight">
              Premio ruta de la tapa de Ceuta
            </h2>
            <p className="text-base md:text-lg mb-6">
              En 2024, Mar de Olivos fue galardonado por su innovación, respeto
              al producto local y fusión de tradición y creatividad. Una
              distinción que refuerza nuestro compromiso con la calidad.
            </p>
          </motion.div>
        </motion.section>

        {/* NEWS */}
        <motion.section
          ref={(el) => (sectionRefs.current[2] = el)}
          id="NEWS"
          className="snap-start scroll-mt-[4px] md:scroll-mt-0 mt-0 md:mt-2 flex items-start md:items-center justify-center
             px-4 md:px-10 pt-12 pb-[max(env(safe-area-inset-bottom),3rem)]"
          style={SECTION_STYLE}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="w-full max-w-5xl bg-[#FAF4EC]/95 rounded-2xl p-3 md:p-4 shadow-xl overflow-hidden transform-gpu
               max-h-[calc(100svh-7.5rem)]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.05 }}
          >
            {/* Layout en pirámide invertida */}
            <div className="grid grid-cols-2 grid-rows-[auto_auto_auto] gap-3 md:gap-4 items-stretch">
              {/* Título */}
              <div className="col-span-2">
                <p className="uppercase tracking-widest text-xs md:text-sm font-bold text-[#ba913d]">
                  Multimedia
                </p>
              </div>

              {/* FILA 1 (ancha): Vídeo local */}
              <button
                type="button"
                onClick={playVideoFullscreen}
                className="col-span-2 rounded-xl overflow-hidden border border-white/5 shadow-lg bg-black/30 group focus:outline-none focus:ring-2 focus:ring-[#C19B50]/50"
                aria-label="Reproducir vídeo en pantalla completa"
              >
                <div className="relative w-full aspect-[16/9]">
                  <video
                    ref={videoRef}
                    src={videomdo}
                    preload="metadata"
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm ring-1 ring-white/20">
                      <svg
                        viewBox="0 0 24 24"
                        className="w-6 h-6 fill-white/90"
                        aria-hidden="true"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  </div>
                </div>
                <div className="p-2 bg-gradient-to-t from-black/10 to-transparent">
                  <h3 className="text-sm md:text-base font-semibold text-[#faf4ec]">
                    Mar de Olivos
                  </h3>
                </div>
              </button>

              {/* FILA 2: Instagram */}
              <a
                href="https://www.instagram.com/reel/DH8s61iKPLs/"
                target="_blank"
                rel="noreferrer"
                className="rounded-xl overflow-hidden border border-white/5 shadow-lg bg-black/30 group"
                aria-label="Abrir reel en Instagram"
              >
                <div className="relative w-full aspect-[16/10]">
                  <img
                    src={torrijas}
                    alt="Instagram - novedades"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="p-3 bg-gradient-to-t from-black/10 to-transparent">
                  <h3 className="text-sm md:text-base font-semibold text-[#faf4ec]">
                    Reel: Torrijas semana santa
                  </h3>
                  <p className="mt-1 text-xs text-white/80">Ver en Instagram</p>
                </div>
              </a>

              {/* FILA 2: Facebook */}
              <a
                href="https://fb.watch/CzmsxdksPg/"
                target="_blank"
                rel="noreferrer"
                className="rounded-xl overflow-hidden border border-white/5 shadow-lg bg-black/30 group"
                aria-label="Abrir vídeo en Facebook"
              >
                <div className="relative w-full aspect-[16/10]">
                  <img
                    src={radiotelevisionceuta}
                    alt="Facebook - novedades"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="p-3 bg-gradient-to-t from-black/10 to-transparent">
                  <h3 className="text-sm md:text-base font-semibold text-[#faf4ec]">
                    Vídeo: Apertura 2022
                  </h3>
                  <p className="mt-1 text-xs text-white/80">Ver en Facebook</p>
                </div>
              </a>
            </div>
          </motion.div>
        </motion.section>

        {/* CREADORES */}
        <motion.section
          ref={(el) => (sectionRefs.current[3] = el)}
          id="creadores"
          className="snap-start mt-0 md:mt-6 mb-18 flex items-start md:items-center justify-center bg-[#0c0c0c]/90 text-[#C19B50] px-6 md:px-16 py-12"
          style={SECTION_STYLE}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="max-w-5xl w-full flex flex-col gap-8"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <h2 className="uppercase tracking-widest text-xl font-bold">
                Hecho con dedicación
              </h2>
              <img
                src={spoon}
                alt="decoración olivo"
                className="w-12 mt-1 mb-2"
              />
            </div>

            <div className="flex flex-col md:flex-row items-center gap-6">
              <Link
                to="/laurita"
                aria-label="Ir a Laurita"
                className="group inline-block"
              >
                <img
                  src={jc}
                  alt="JC"
                  className="w-36 h-36 md:w-44 md:h-44 object-cover rounded-full border-2 border-[#C19B50] transition group-hover:scale-[1.03] group-hover:shadow-lg"
                />
              </Link>

              <div className="text-center md:text-left">
                <h3 className="text-2xl font-semibold">Juan Carlos López</h3>
                <p className="text-white text-sm">
                  Diseñador gráfico y desarrollador web
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row-reverse items-center gap-4 md:gap-6">
              <img
                src={david}
                alt="David"
                className="w-36 h-36 md:w-44 md:h-44 object-cover rounded-full border-2 border-[#C19B50]"
              />
              <div className="text-center md:text-right">
                <h3 className="text-2xl font-semibold">David Carmona</h3>
                <p className="text-white text-sm">Técnico de la plataforma</p>
              </div>
            </div>
          </motion.div>
        </motion.section>
      </div>
    </div>
  );
};

export default Home;
